import { MissingIndexedShapeError } from '@takeshape/errors';
import { ensureQuery, findShapeAtPath, isObjectSchema, returnShapeToSchema } from '@takeshape/schema';
import { parse, print, visit } from 'graphql';
import { JSONPath as jsonPath } from 'jsonpath-plus';
import range from 'lodash/range.js';
import pMap from 'p-map';
import { appendDefinitions, createSchemaInfoVisitor, ensureIds, getFragmentsUsed, getOperation, setSelectionSet, visitWithSchemaInfo } from "./ast-utils.js";
import { getGraphQLQueryString } from "./graphql-helpers.js";
import { jsonPathToArray } from "./pagination/index.js";
function getPredicate(queryConfig) {
    if (queryConfig.ignoreFields) {
        const blacklist = new Set(queryConfig.ignoreFields);
        return ({ propName }) => !propName || !blacklist.has(propName);
    }
}
export function getIndexingQueryString(projectSchema, queryConfig, queryPathToItems, queryArgs) {
    let args = queryArgs;
    if (queryConfig.args) {
        args = { ...queryConfig.args, ...args };
    }
    // Default max depth is 2 but if we have selectionSet use 1 to create a base query to override
    const maxQueryDepth = queryConfig.maxDepth ?? (queryConfig.selectionSet ? 1 : 2);
    const queryString = getGraphQLQueryString(projectSchema, queryConfig.query, {
        predicate: getPredicate(queryConfig),
        maxQueryDepth,
        args,
        includeDeprecated: queryConfig.includeDeprecated ?? false,
        onlyFormProps: false
    });
    const { name } = ensureQuery(projectSchema, queryConfig.query);
    if (queryConfig.selectionSet) {
        const shouldMerge = queryConfig.maxDepth !== undefined;
        const customSelectionDoc = parse(queryConfig.selectionSet, {
            noLocation: true
        });
        const customSelectionSetNode = getOperation(customSelectionDoc)?.selectionSet;
        const baseQueryDoc = parse(queryString, { noLocation: true });
        const queryDoc = appendDefinitions(setSelectionSet(baseQueryDoc, [name, ...queryPathToItems], customSelectionSetNode, shouldMerge), getFragmentsUsed(customSelectionDoc));
        const schemaInfoVisitor = createSchemaInfoVisitor(projectSchema);
        const visitor = visitWithSchemaInfo(schemaInfoVisitor, ensureIds(projectSchema, schemaInfoVisitor, {
            idField: queryConfig.idField,
            shapeId: true
        }));
        return print(visit(queryDoc, visitor));
    }
    return queryString;
}
function isJsonValue(value) {
    return value !== undefined;
}
function* yieldDeduplicated(items, seenIds, deduplicateBy) {
    for (const item of items) {
        const id = item[deduplicateBy];
        if (!id || !seenIds.has(id)) {
            if (id)
                seenIds.add(id);
            yield item;
        }
    }
}
export async function* paginator({ projectSchema, queryConfig, connector, paginationHelper, errorCallback, firstQueryCallback, abortSignal, onPageComplete, deduplicateBy = '_id' }) {
    const { name, query } = ensureQuery(projectSchema, queryConfig.query);
    const returnShapeSchema = returnShapeToSchema(projectSchema, query.shape);
    const queryPathToItems = jsonPathToArray(paginationHelper.itemsPath);
    const indexedShape = findShapeAtPath(projectSchema, returnShapeSchema, queryPathToItems);
    if (!indexedShape) {
        throw new MissingIndexedShapeError(queryConfig.query);
    }
    if (!isObjectSchema(indexedShape.schema)) {
        throw new Error(`Paginated shape ${indexedShape.name} is not an object`);
    }
    const { itemsPath, hasMore, getState, getTotalPages, getArgsForPage, getTotalItems, concurrency } = paginationHelper;
    const seenIds = new Set();
    // Check for abort before starting
    if (abortSignal?.aborted) {
        return;
    }
    const buildQuery = (args) => getIndexingQueryString(projectSchema, queryConfig, queryPathToItems, args);
    const fetchPage = async (queryString) => {
        const rawResult = await connector({ query: queryString });
        if (rawResult.errors?.length) {
            return { success: false, queryString, rawResult, error: 'queryError' };
        }
        const pageData = rawResult.data?.[name];
        const items = isJsonValue(pageData) && jsonPath({ json: pageData, path: itemsPath, wrap: false });
        if (!items || !Array.isArray(items)) {
            return { success: false, queryString, rawResult, error: 'noData' };
        }
        return { success: true, queryString, rawResult, pageData, items };
    };
    // Fetch first page
    const firstQueryString = buildQuery(paginationHelper.getArgs(undefined));
    firstQueryCallback?.(firstQueryString);
    const firstPage = await fetchPage(firstQueryString);
    if (!firstPage.success) {
        errorCallback?.({ query: firstQueryString }, firstPage.rawResult, firstPage.error);
        return;
    }
    // Extract totals from first response if the helper supports it
    const totalPages = getTotalPages?.(firstPage.pageData);
    const preciseTotalItems = getTotalItems?.(firstPage.pageData);
    const totalItems = preciseTotalItems ?? (totalPages ? totalPages * firstPage.items.length : undefined);
    const isTotalItemsEstimated = totalItems !== undefined && preciseTotalItems === undefined;
    // Enrich state with totals derived from the first page
    const enrichState = () => ({
        ...getState(),
        totalPages,
        totalItems,
        isTotalItemsEstimated: isTotalItemsEstimated || undefined
    });
    yield* yieldDeduplicated(firstPage.items, seenIds, deduplicateBy);
    onPageComplete?.(enrichState(), firstPage.items.length);
    if (abortSignal?.aborted) {
        return;
    }
    const useParallel = Boolean(concurrency && concurrency > 0 && getTotalPages && getArgsForPage && totalPages && totalPages > 1);
    // Check if parallel mode is available (requires concurrency > 0 and parallel-capable helper)
    if (useParallel && getArgsForPage && totalPages) {
        // Parallel mode: fetch pages 2..N with concurrency limit
        const remainingPageNumbers = range(2, totalPages + 1);
        const pageResults = await pMap(remainingPageNumbers, async (pageNum) => fetchPage(buildQuery(getArgsForPage(pageNum))), { concurrency, stopOnError: false });
        // Process results in page order for deterministic output
        for (const page of pageResults) {
            if (abortSignal?.aborted)
                break;
            if (!page.success) {
                errorCallback?.({ query: page.queryString }, page.rawResult, page.error);
                break;
            }
            yield* yieldDeduplicated(page.items, seenIds, deduplicateBy);
            onPageComplete?.(enrichState(), page.items.length);
        }
        return;
    }
    // Sequential fallback (cursor or offset without total, or single page result)
    let currentPageData = firstPage.pageData;
    while (hasMore(currentPageData)) {
        if (abortSignal?.aborted)
            break;
        const page = await fetchPage(buildQuery(paginationHelper.getArgs(currentPageData)));
        if (!page.success) {
            errorCallback?.({ query: page.queryString }, page.rawResult, page.error);
            break;
        }
        yield* yieldDeduplicated(page.items, seenIds, deduplicateBy);
        onPageComplete?.(enrichState(), page.items.length);
        if (abortSignal?.aborted)
            break;
        currentPageData = page.pageData;
    }
}
